import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { openModal } from "../../redux/features/modalSlice";
import { GetModuleFeatureList } from "../../redux/services/moduleService/module.Services";

const ViewModuleFeatures = ({ data }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) fetchModule(id);
  }, [id]);

  const fetchModule = async (moduleId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await GetModuleFeatureList({ module_id: moduleId });
      setModule(result?.data || null);
      console.log("API Result:", result);
    } catch (err) {
      console.error("Error fetching module:", err);
      setError("Failed to load module details.");
    } finally {
      setLoading(false);
    }
  };

  const stripHtmlTags = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const getIconClass = (icon) => {
    if (!icon) return "";
    const brandIcons = [
      "fa-facebook",
      "fa-facebook-f",
      "fa-instagram",
      "fa-google",
      "fa-whatsapp",
      "fa-youtube",
      "fa-twitter",
      "fa-linkedin",
      "fa-apple",
      "fa-android",
      "fa-x-twitter",
    ];
    const style = brandIcons.includes(icon) ? "fab" : "fas";
    return `${style} ${icon}`;
  };

  /* ---------- Subscribe handler ---------- */
  const handleSubscribe = () => {
      const moduleData = Array.isArray(data) ? data[0] : data;
    dispatch(
      openModal({
        type: "add-vendor-enquiry",
        title: "Enquiry",
        data: { module: moduleData,
           module_id: moduleData?.id || id || null,
        module_name: moduleData?.name || module?.name || null,
        },
      })
    );
  };

  /* ---------- LOADING ---------- */
  if (loading) {
    return (
      <div className="vmf-wrapper">
        <div className="vmf-container">
          <div className="vmf-section">
            <div className="vmf-header">
              <div className="vmf-skeleton" style={{ width: 240, height: 26 }} />
              <button className="vmf-back-btn" onClick={() => navigate(-1)}>
                Back
              </button>
            </div>
            {[...Array(4)].map((_, i) => (
              <div key={i} className="mb-3">
                <div
                  className="vmf-skeleton"
                  style={{ width: "35%", height: 16 }}
                />
                <div
                  className="vmf-skeleton mt-2"
                  style={{ width: "70%", height: 14 }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ---------- ERROR ---------- */
  if (error) {
    return (
      <div className="vmf-wrapper">
        <div className="vmf-container">
          <div className="vmf-section">
            <div className="vmf-header">
              <h3 className="vmf-header-title">Module Features</h3>
              <button className="vmf-back-btn" onClick={() => navigate(-1)}>
                Back
              </button>
            </div>
            <div className="alert alert-danger mb-0">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- NO DATA ---------- */
  if (!module) {
    return (
      <div className="vmf-wrapper">
        <div className="vmf-container">
          <div className="vmf-section">
            <div className="vmf-header">
              <h3 className="vmf-header-title">Module Features</h3>
              <button className="vmf-back-btn" onClick={() => navigate(-1)}>
                Back
              </button>
            </div>
            <p className="text-muted mb-0">No module details found.</p>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- DATA EXTRACTION ---------- */
  const solution = module?.solutions?.[0] || {};
  const sections = (solution?.sections || [])
    .slice()
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const visibleSections = sections.filter(
    (s) => s.items && s.items.length > 0
  );

  const getGridClass = (count) => {
    if (count <= 2) return "vmf-grid vmf-grid-2";
    if (count === 3) return "vmf-grid vmf-grid-3";
    return "vmf-grid";
  };

  /* ---------- MAIN ---------- */
  return (
    <div className="vmf-wrapper">
      <div className="vmf-container">
        {/* PAGE HEADER */}
        <div className="vmf-section">
          <div className="vmf-header">
            <div className="vmf-header-left">
              <h2 className="vmf-header-title">{module.name}</h2>
              {module.description && (
                <p className="vmf-header-desc">{module.description}</p>
              )}
            </div>
            <div className="vmf-header-actions">
              <button className="vmf-back-btn" onClick={() => navigate(-1)}>
                Back
              </button>
              <button
                className="vmf-btn vmf-btn-primary"
                onClick={handleSubscribe}
              >
                Enquiry  Now
              </button>
            </div>
          </div>
        </div>

        {/* SECTIONS */}
        {visibleSections.map((section) => (
          <div key={section.id} className="vmf-section">
            <h3 className="vmf-section-title">{section.title}</h3>
            {section.description && (
              <p className="vmf-section-desc">
                {stripHtmlTags(section.description)}
              </p>
            )}

            <div className={getGridClass(section.items.length)}>
              {section.items.map((item) => (
                <div key={item.id} className="vmf-card">
                  <div className="vmf-card-inner">
                    <div className="vmf-icon">
                      {item.icon ? (
                        <i
                          className={getIconClass(item.icon)}
                          aria-hidden="true"
                        />
                      ) : (
                        "✓"
                      )}
                    </div>
                    <div>
                      <h4 className="vmf-card-title">{item.title}</h4>
                      {item.description && (
                        <p className="vmf-card-desc">
                          {stripHtmlTags(item.description)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* FOOTER BUTTONS */}
        <div className="vmf-footer">
          <button className="vmf-btn" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button
            className="vmf-btn vmf-btn-primary"
            onClick={handleSubscribe}
          >
            Enquiry Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewModuleFeatures;